import { useState, useEffect } from "react";
import { formatCurrency, formatShortDateOnly as formatPureDate } from "@/lib/format-utils";
import { PERIL_TYPES, US_STATES } from "@/lib/enum-utils";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { queryClient } from "@/lib/query-client";
import { useLocation, useSearch, Link } from "wouter";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { PaginationControls } from "@/components/ui/pagination-controls";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ClaimsTableSkeleton } from "./claims-table-skeleton";
import NewClaimModal from "./modals/new-claim-modal";
import { useEditClaimPopup } from "@/hooks/use-edit-claim-popup";
import { usePermissions } from "@/hooks/use-permissions";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Plus,
  Search,
  Eye,
  Edit,
  Mic,
  MessageSquare,
  FileText,
  MoreHorizontal,
  Archive,
  Upload,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface ClaimsTableProps {
  fullPage?: boolean;
}

export default function ClaimsTable({ fullPage = false }: ClaimsTableProps) {
  const [, setLocation] = useLocation();
  const searchString = useSearch();
  const permissions = usePermissions();
  const isMobile = useIsMobile();
  const { openEditClaim } = useEditClaimPopup();
  
  // Parse URL query parameters
  const statusFromUrl = new URLSearchParams(searchString).get('status');

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(statusFromUrl || "active");
  const [activeTab, setActiveTab] = useState<"active" | "archive">("active");
  const [perilFilter, setPerilFilter] = useState("all");
  const [adjusterFilter, setAdjusterFilter] = useState("all");
  const [adjusterRoleFilter, setAdjusterRoleFilter] = useState("all");
  const [referrerFilter, setReferrerFilter] = useState("all");
  const [carrierFilter, setCarrierFilter] = useState("all");
  const [stateFilter, setStateFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [showNewClaimModal, setShowNewClaimModal] = useState(false);


  // Effect 1: Sync URL params to filter state (no page reset here — Effect 2 handles it)
  useEffect(() => {
    const params = new URLSearchParams(searchString);
    setStatusFilter(params.get('status') || 'active');
  }, [searchString]);

  // Effect 2: Debounce search input — only propagate to query after 300ms of inactivity
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Effect 3: Reset to page 1 when filters or page size change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, perilFilter, adjusterFilter, adjusterRoleFilter, referrerFilter, carrierFilter, stateFilter, limit, activeTab]);

  const offset = (page - 1) * limit;

  // Derive effective status for the query.
  // On active tab: use statusFilter (defaults to "active" which excludes closed).
  // On archive tab: always "closed" regardless of statusFilter dropdown.
  const effectiveStatus = activeTab === "archive" ? "closed" : statusFilter;

  const { data: claimsData, isLoading, isPlaceholderData } = useQuery({
    queryKey: ["/api/claims", { search: debouncedSearch, status: effectiveStatus, peril: perilFilter, adjusterUserId: adjusterFilter, adjusterRole: adjusterRoleFilter, referrerId: referrerFilter, carrierId: carrierFilter, propertyState: stateFilter, limit, offset }],
    refetchInterval: 30000,
    enabled: permissions.canReadClaims,
    placeholderData: keepPreviousData,
  });

  const { data: users } = useQuery({ queryKey: ["/api/users/adjusters"] });
  const { data: referrers } = useQuery({ queryKey: ["/api/claims/referrers"], enabled: permissions.canReadClaims });
  const { data: carriers } = useQuery({ queryKey: ["/api/claims/carriers"], enabled: permissions.canReadClaims });

  const claims = (claimsData as any)?.claims || [];
  const total = (claimsData as any)?.total || 0;
  const totalPages = Math.ceil(total / limit);

  // Clamp page to valid range when total changes (e.g., items deleted from last page)
  useEffect(() => {
    if (totalPages > 0 && page > totalPages) {
      setPage(totalPages);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total, totalPages]);

  const handleViewClaim = (claimId: string) => {
    setLocation(`/claims/${claimId}`);
  };

  const handleEditClaim = (claim: any) => {
    openEditClaim({
      claimId: claim.id,
      claim,
      onSaved: () => {
        queryClient.invalidateQueries({ queryKey: ["/api/claims"] });
      },
    });
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const getStatusColor = (status: string) => {
    const colorMap: Record<string, string> = {
      evaluation: 'border-l-blue-400',
      open: 'border-l-green-500',
      in_progress: 'border-l-yellow-500',
      closed: 'border-l-slate-400',
      settled: 'border-l-purple-500',
      on_hold: 'border-l-orange-400',
      litigation: 'border-l-red-500',
    };
    return colorMap[status] || 'border-l-primary';
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      open: { variant: "default" as const, color: "bg-green-100 text-green-800 dark:bg-green-500/10 dark:text-green-300" },
      in_progress: { variant: "secondary" as const, color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-500/10 dark:text-yellow-300" },
      closed: { variant: "outline" as const, color: "bg-blue-100 text-blue-800 dark:bg-blue-500/10 dark:text-blue-300" },
      settled: { variant: "default" as const, color: "bg-purple-100 text-purple-800 dark:bg-purple-500/10 dark:text-purple-300" },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.open;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
      </span>
    );
  };



  const formatAddress = (claim: any) => {
    const parts = [];
    if (claim.propertyCity) parts.push(claim.propertyCity);
    if (claim.propertyState) parts.push(claim.propertyState);
    if (claim.propertyZip) parts.push(claim.propertyZip);
    
    if (parts.length === 0) return "";
    
    // Format as "City, State Zip"
    if (claim.propertyCity && claim.propertyState) {
      return `${claim.propertyCity}, ${claim.propertyState}${claim.propertyZip ? ' ' + claim.propertyZip : ''}`;
    }
    // Just state and zip
    return parts.join(' ');
  };

  const getInsuredName = (claim: any) => {
    if (claim.claimType === 'commercial') {
      if (claim.policyholderCompany) return claim.policyholderCompany;
      if (claim.pointOfContact) return claim.pointOfContact;
      return "Unknown (Commercial)";
    }
    
    if (claim.policyholderFirstName || claim.policyholderLastName) {
      return `${claim.policyholderFirstName || ''} ${claim.policyholderLastName || ''}`.trim();
    }
    
    return "Unknown";
  };

  // Mobile stacked card view renderer
  const renderMobileCard = (claim: any) => (
    <div
      key={claim.id}
      className={`mobile-stacked-card rounded-lg border bg-card text-card-foreground shadow-sm mb-3 border-l-4 ${getStatusColor(claim.status)}`}
      data-testid={`card-claim-${claim.id}`}
    >
      {/* Card Header — Claim # + Status */}
      <div className="flex items-center justify-between px-3 py-3 border-b border-border">
        <Link
          href={`/claims/${claim.id}`}
          className="text-sm font-bold text-primary hover:underline truncate"
          data-testid={`link-claim-number-${claim.id}`}
        >
          {claim.claimNumber}
        </Link>
        {getStatusBadge(claim.status)}
      </div>

      {/* Stacked Label → Value Rows */}
      <div className="px-3 py-3 space-y-1.5">
        {/* Insured */}
        <div className="grid grid-cols-[5.5rem_1fr] gap-x-1 items-baseline">
          <span className="text-xs font-medium text-muted-foreground">Insured</span>
          <Link
            href={`/claims/${claim.id}`}
            className="text-sm font-semibold text-foreground hover:underline text-left truncate"
            data-testid={`link-insured-name-${claim.id}`}
          >
            {getInsuredName(claim)}
          </Link>
        </div>

        {/* Property */}
        <div className="grid grid-cols-[5.5rem_1fr] gap-x-1 items-baseline">
          <span className="text-xs font-medium text-muted-foreground">Property</span>
          <div className="text-sm text-foreground">
            {claim.propertyAddress ? (
              <>
                <div className="font-medium">{claim.propertyAddress}</div>
                {formatAddress(claim) && (
                  <div className="text-xs text-muted-foreground">{formatAddress(claim)}</div>
                )}
              </>
            ) : formatAddress(claim) ? (
              <div className="font-medium">{formatAddress(claim)}</div>
            ) : (
              <span className="text-muted-foreground italic">No address</span>
            )}
          </div>
        </div>

        {/* Peril */}
        <div className="grid grid-cols-[5.5rem_1fr] gap-x-1 items-baseline">
          <span className="text-xs font-medium text-muted-foreground">Peril</span>
          <span className="text-sm text-foreground capitalize">{claim.perilType || '—'}</span>
        </div>

        {/* Date of Loss */}
        <div className="grid grid-cols-[5.5rem_1fr] gap-x-1 items-baseline">
          <span className="text-xs font-medium text-muted-foreground">Date of Loss</span>
          <span className="text-sm text-foreground">{formatPureDate(claim.dateOfLoss)}</span>
        </div>

        {/* Estimated Value */}
        <div className="grid grid-cols-[5.5rem_1fr] gap-x-1 items-baseline">
          <span className="text-xs font-medium text-muted-foreground">Est. Value</span>
          <span className="text-sm font-semibold text-foreground">{formatCurrency(claim.estimatedValue)}</span>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-1.5 px-3 py-2 border-t border-border bg-muted/30 rounded-b-lg">
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleViewClaim(claim.id)}
          data-testid={`button-view-claim-${claim.id}`}
          className="flex-1"
        >
          <Eye className="h-4 w-4 mr-1" />
          View
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleEditClaim(claim)}
          data-testid={`button-edit-claim-${claim.id}`}
          className="flex-1"
        >
          <Edit className="h-4 w-4 mr-1" />
          Edit
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              data-testid={`button-more-actions-${claim.id}`}
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => window.open(`/apps/ai-drafting?claimId=${claim.id}`, '_blank')}
              data-testid={`button-ai-document-draft-${claim.id}`}
            >
              <FileText className="h-4 w-4 mr-2" />
              AI Document Drafting
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => window.open(`/apps/voice-recorder?claimId=${claim.id}`, '_blank')}
              data-testid={`button-voice-record-${claim.id}`}
            >
              <Mic className="h-4 w-4 mr-2" />
              Voice Recorder
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => window.open(`/apps/nlp-search?claimId=${claim.id}`, '_blank')}
              data-testid={`button-nlp-search-${claim.id}`}
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              NLP Search
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );

  return (
    <>
      <Card>
        <CardHeader className="space-y-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <CardTitle className="text-base sm:text-lg font-semibold text-foreground">
              {fullPage
                ? activeTab === "archive" ? "Archived Claims" : "Claims Management"
                : "Recent Claims"}
              {!permissions.canReadAllClaims && permissions.canReadAssignedClaims && (
                <span className="block sm:inline text-xs sm:text-sm font-normal text-muted-foreground sm:ml-2 mt-1 sm:mt-0">(Assigned Claims Only)</span>
              )}
            </CardTitle>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
              {permissions.isAdmin && (
                <Button
                  variant="outline"
                  onClick={() => setLocation('/apps/claim-import')}
                  data-testid="button-bulk-import"
                  className="w-full sm:w-auto"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Bulk Import
                </Button>
              )}
              {permissions.canWriteClaims && (
                <Button
                  onClick={() => setShowNewClaimModal(true)}
                  data-testid="button-new-claim"
                  className="w-full sm:w-auto"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New Claim
                </Button>
              )}
            </div>
          </div>
          
          {fullPage && (
            <div className="flex items-center gap-3 mb-2">
              <Tabs
                value={activeTab}
                onValueChange={(v) => {
                  setActiveTab(v as "active" | "archive");
                  // Reset status filter to default when switching tabs
                  setStatusFilter(v === "archive" ? "closed" : "active");
                }}
              >
                <TabsList>
                  <TabsTrigger value="active" data-testid="tab-active-claims">
                    Active Claims
                  </TabsTrigger>
                  <TabsTrigger value="archive" data-testid="tab-archived-claims">
                    <Archive className="h-3.5 w-3.5 mr-1.5" />
                    Archived
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          )}

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
            <div className="flex-1 relative">
              <Input
                placeholder="Search by name, phone, address, zip, claim #..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="input-search-claims"
              />
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48 lg:w-56" data-testid="select-status-filter">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value={activeTab === "archive" ? "closed" : "active"}>
                  {activeTab === "archive" ? "All Closed" : "All Active"}
                </SelectItem>
                {activeTab !== "archive" && (
                  <>
                    <SelectItem value="open">Open (All Active)</SelectItem>
                    <SelectItem value="evaluation">Evaluation</SelectItem>
                    <SelectItem value="claim_accepted_sales">Claim Accepted (Sales)</SelectItem>
                    <SelectItem value="need_nts_repairability_video_sales">Need NTS/Repairability Video</SelectItem>
                    <SelectItem value="need_pa_docs_returned_sales">Need PA Docs Returned</SelectItem>
                    <SelectItem value="need_lor_emailed_to_ic_sales">Need LOR Emailed to IC</SelectItem>
                    <SelectItem value="need_inspection_field">Need Inspection (Field)</SelectItem>
                    <SelectItem value="need_measurements_field">Need Measurements</SelectItem>
                    <SelectItem value="need_photos_field">Need Photos</SelectItem>
                    <SelectItem value="need_quotes_invoices_ctr_field">Need Quotes/Invoices</SelectItem>
                    <SelectItem value="need_estimate_field">Need Estimate</SelectItem>
                    <SelectItem value="need_pol_sent_to_ph_sales">Need POL Sent to PH</SelectItem>
                    <SelectItem value="need_pol_returned_sales">Need POL Returned</SelectItem>
                    <SelectItem value="need_claim_package_uploaded_field">Need Claim Package Uploaded</SelectItem>
                    <SelectItem value="need_estimate_review_optional">Need Estimate Review</SelectItem>
                    <SelectItem value="notifying_insurance_send_claim_package_desk">Notifying Insurance</SelectItem>
                    <SelectItem value="initial_negotiation_desk">Initial Negotiation</SelectItem>
                    <SelectItem value="escalated_negotiations">Escalated Negotiations</SelectItem>
                    <SelectItem value="pending_1st_inspection_field">Pending 1st Inspection</SelectItem>
                    <SelectItem value="pending_2nd_inspection_field">Pending 2nd Inspection</SelectItem>
                    <SelectItem value="pending_engineer_inspection_field">Pending Engineer Inspection</SelectItem>
                    <SelectItem value="final_demand_desk">Final Demand</SelectItem>
                    <SelectItem value="doi_complaint_desk">DOI Complaint</SelectItem>
                    <SelectItem value="contractor_in_production_admin">Contractor in Production</SelectItem>
                    <SelectItem value="contractor_needs_supplement">Contractor Needs Supplement</SelectItem>
                    <SelectItem value="need_depreciation_released_desk">Need Depreciation Released</SelectItem>
                    <SelectItem value="pending_final_payment">Pending Final Payment</SelectItem>
                    <SelectItem value="litigation">Litigation</SelectItem>
                    <SelectItem value="appraisal_we_are_pa">Appraisal (We Are PA)</SelectItem>
                    <SelectItem value="appraisal_file_we_are_appraiser">Appraisal File (We Are Appraiser)</SelectItem>
                    <SelectItem value="on_hold">On Hold</SelectItem>
                    <SelectItem value="collections_accounting">Collections/Accounting</SelectItem>
                    <SelectItem value="need_to_close_claim">Need to Close Claim</SelectItem>
                    <SelectItem value="needs_mgmt_review">Needs Mgmt Review</SelectItem>
                    <SelectItem value="settled">Settled</SelectItem>
                  </>
                )}
                {activeTab === "archive" && (
                  <SelectItem value="closed">Closed</SelectItem>
                )}
              </SelectContent>
            </Select>
            
            <Select value={perilFilter} onValueChange={setPerilFilter}>
              <SelectTrigger className="w-full sm:w-40 lg:w-48" data-testid="select-peril-filter">
                <SelectValue placeholder="All Perils" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Perils</SelectItem>
                {PERIL_TYPES.map(p => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 flex-wrap">
            <Select value={adjusterFilter} onValueChange={setAdjusterFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="select-adjuster-filter">
                <SelectValue placeholder="All Adjusters" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Adjusters</SelectItem>
                {(users as any[])?.map((u: any) => (
                  <SelectItem key={u.id} value={u.id}>{u.firstName} {u.lastName}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={adjusterRoleFilter} onValueChange={setAdjusterRoleFilter}>
              <SelectTrigger className="w-full sm:w-44" data-testid="select-adjuster-role-filter">
                <SelectValue placeholder="All Roles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="sales_pa">Sales PA</SelectItem>
                <SelectItem value="field_pa">Field PA</SelectItem>
                <SelectItem value="desk_pa">Desk PA</SelectItem>
                <SelectItem value="consultant">Consultant</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>

            <Select value={referrerFilter} onValueChange={setReferrerFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="select-referrer-filter">
                <SelectValue placeholder="All Referrers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Referrers</SelectItem>
                {(referrers as {id: string, name: string}[])?.map((r) => (
                  <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={carrierFilter} onValueChange={setCarrierFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="select-carrier-filter">
                <SelectValue placeholder="All Carriers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Carriers</SelectItem>
                {(carriers as {id: string, name: string}[])?.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={stateFilter} onValueChange={setStateFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="select-state-filter">
                <SelectValue placeholder="All States" />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="all">All States</SelectItem>
                {US_STATES.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        
        <CardContent>
          <div style={{ minHeight: `${limit * 52}px` }}>
            {permissions.isLoading ? (
              <ClaimsTableSkeleton rows={limit} isMobile={isMobile} />
            ) : !permissions.canReadClaims ? (
              <div className="text-center py-8 text-muted-foreground">
                <div className="text-lg font-medium mb-2">Access Restricted</div>
                <div>You don't have permission to view claims. Please contact your administrator.</div>
              </div>
            ) : isLoading ? (
              <ClaimsTableSkeleton rows={limit} isMobile={isMobile} />
            ) : !claims.length ? (
              <div className="text-center py-8 text-muted-foreground" data-testid="empty-claims">
                {!permissions.canReadAllClaims && permissions.canReadAssignedClaims
                  ? "No claims assigned to you yet. Create a claim or wait for an assignment."
                  : "No claims yet. Create your first claim, or connect Gmail on the dashboard to pull claims in from email."
                }
                <div className="mt-4">
                  <Button onClick={() => setShowNewClaimModal(true)} data-testid="empty-claims-create">
                    Create the first claim
                  </Button>
                </div>
              </div>
            ) : (
              <motion.div
                key={page}
                initial={{ opacity: 0.6 }}
                animate={{ opacity: isPlaceholderData ? 0.6 : 1 }}
                transition={{ duration: 0.15, ease: "easeOut" }}
              >
                {/* Mobile card view */}
                <div className="block sm:hidden">
                  {claims.map((claim: any) => renderMobileCard(claim))}
                </div>

                {/* Tablet & Desktop table view */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted">
                      <tr>
                        <th className="text-left py-3 px-4 lg:px-6 text-sm font-medium text-muted-foreground">Claim #</th>
                        <th className="text-left py-3 px-4 lg:px-6 text-sm font-medium text-muted-foreground">Insured</th>
                        <th className="text-left py-3 px-4 lg:px-6 text-sm font-medium text-muted-foreground">Property</th>
                        <th className="text-left py-3 px-4 lg:px-6 text-sm font-medium text-muted-foreground">Peril</th>
                        <th className="text-left py-3 px-4 lg:px-6 text-sm font-medium text-muted-foreground">Status</th>
                        <th className="text-left py-3 px-4 lg:px-6 text-sm font-medium text-muted-foreground">Date of Loss</th>
                        <th className="text-left py-3 px-4 lg:px-6 text-sm font-medium text-muted-foreground">Est. Value</th>
                        <th className="text-left py-3 px-4 lg:px-6 text-sm font-medium text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {claims.map((claim: any) => (
                        <tr key={claim.id} className="hover:bg-muted/50" data-testid={`row-claim-${claim.id}`}>
                          <td className="py-3 px-4 lg:py-4 lg:px-6 text-sm font-medium text-primary">
                            <Link
                              href={`/claims/${claim.id}`}
                              className="hover:underline cursor-pointer"
                              data-testid={`link-claim-number-${claim.id}`}
                            >
                              {claim.claimNumber}
                            </Link>
                          </td>
                          <td className="py-3 px-4 lg:py-4 lg:px-6 text-sm text-foreground">
                            <Link
                              href={`/claims/${claim.id}`}
                              className="hover:underline cursor-pointer font-medium"
                              data-testid={`link-insured-name-${claim.id}`}
                            >
                              {getInsuredName(claim)}
                            </Link>
                          </td>
                          <td className="py-3 px-4 lg:py-4 lg:px-6 text-sm text-foreground">
                            <div>
                              {claim.propertyAddress && (
                                <div className="font-medium">{claim.propertyAddress}</div>
                              )}
                              {formatAddress(claim) && (
                                <div className="text-muted-foreground text-xs">
                                  {formatAddress(claim)}
                                </div>
                              )}
                              {!claim.propertyAddress && !formatAddress(claim) && (
                                <span className="text-muted-foreground italic">No address</span>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4 lg:py-4 lg:px-6 text-sm text-foreground capitalize">
                            {claim.perilType}
                          </td>
                          <td className="py-3 px-4 lg:py-4 lg:px-6">
                            {getStatusBadge(claim.status)}
                          </td>
                          <td className="py-3 px-4 lg:py-4 lg:px-6 text-sm text-muted-foreground">
                            {formatPureDate(claim.dateOfLoss)}
                          </td>
                          <td className="py-3 px-4 lg:py-4 lg:px-6 text-sm font-medium text-foreground">
                            {formatCurrency(claim.estimatedValue)}
                          </td>
                          <td className="py-3 px-4 lg:py-4 lg:px-6">
                            <div className="flex items-center space-x-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewClaim(claim.id)}
                                data-testid={`button-view-claim-${claim.id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditClaim(claim)}
                                title="Edit Claim"
                                data-testid={`button-edit-claim-${claim.id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => window.open(`/apps/ai-drafting?claimId=${claim.id}`, '_blank')}
                                title="AI Document Drafting - Generate formal letters & reports"
                                data-testid={`button-ai-document-draft-${claim.id}`}
                              >
                                <FileText className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => window.open(`/apps/voice-recorder?claimId=${claim.id}`, '_blank')}
                                title="Voice Recorder"
                                data-testid={`button-voice-record-${claim.id}`}
                              >
                                <Mic className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => window.open(`/apps/nlp-search?claimId=${claim.id}`, '_blank')}
                                title="NLP Search"
                                data-testid={`button-nlp-search-${claim.id}`}
                              >
                                <MessageSquare className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            )}
          </div>

          {total > 0 && (
            <PaginationControls
              page={page}
              totalPages={totalPages}
              totalItems={total}
              currentPageItemCount={claims.length}
              pageSize={limit}
              onPageChange={handlePageChange}
              onPageSizeChange={(size) => setLimit(size)}
            />
          )}
        </CardContent>
      </Card>

      <NewClaimModal 
        open={showNewClaimModal} 
        onOpenChange={setShowNewClaimModal}
      />
      
    </>
  );
}
